import axiosInstance, { axiosInstanceCached } from '@/utils/axios';
import { handleApiError, deleteDataCache } from '@/utils/helpers';

// @ API_HOST is set in axiosInstance by default
// @ /webstore/products?category=mopContent&type=subscription&promotion=true

//* General delay utility function
function delay(timeout) {
  return new Promise((resolve) => {
    const timeoutHandle = setTimeout(() => {
      clearTimeout(timeoutHandle);
      resolve();
    }, timeout);
  });
}

export const list = async (payload) => {
  const {
    category,
    skucode,
    filter: type,
    promotion,
    all = false,
    store_version = 1,
  } = payload || {};
  let response = {};
  const params = new URLSearchParams();
  let types;
  let categories;

  if (!all) {
    params.append('isWeb', true);
  }
  if (category) {
    categories = category.split(',');
    categories.forEach((cat) => {
      params.append('category', cat);
    });
  }
  if (type) {
    types = type.split(',');
    types.forEach((item, index) => {
      params.append('type', item);
    });
  }
  if (promotion) {
    params.append('promotion', promotion);
  }
  if (skucode) {
    params.append('skucode', skucode);
  }
  params.append('store_version', store_version);

  try {
    response = await axiosInstance.get(`/webstore/products`, {
      params,
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const show = async (id) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/webstore/products/${id}`);
    // response = await axiosInstanceCached.get(`/webstore/products/${id}`, {
    //   id: `product-_${id}`,
    //   cache: {
    //     ttl: 1000 * 60 * 5,
    //   },
    // });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const showSku = async (sku) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/webstore/products/sku/${sku}`);
    // response = await axiosInstanceCached.get(`/webstore/products/sku/${sku}`, {
    //   id: `product-_${sku}`,
    //   cache: {
    //     ttl: 1000 * 60 * 5,
    //   },
    // });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const createCart = async () => {
  let response = {};

  try {
    response = await axiosInstance.post(`/webstore/cart`, null);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getCart = async () => {
  let response = {};

  try {
    response = await axiosInstance.get(`/webstore/cart`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteCart = async () => {
  let response = {};

  try {
    response = await axiosInstance.delete(`/webstore/cart`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ bypassDuplicateCheck - used for bypassing the check for multiple products within 15 minutes
// @ bypassAddToCartPayAndPauseCheck - used for bypassing the pay and pause check. Primarily for dual type product keys with a subscription
export const addToCart = async (payload) => {
  let response = {};
  const { bypassDuplicateCheck, bypassAddToCartPayAndPauseCheck } = payload;
  const url = `webstore/cart/item${
    bypassDuplicateCheck ? `?bypassDuplicateCheck=${bypassDuplicateCheck}` : ''
  }${
    bypassAddToCartPayAndPauseCheck
      ? `&bypassAddToCartPayAndPauseCheck=${bypassAddToCartPayAndPauseCheck}`
      : ''
  }`;

  try {
    response = await axiosInstance.post(url, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateCartItem = async (payload) => {
  let response = {};
  const { itemID } = payload;

  try {
    response = await axiosInstance.patch(`/webstore/cart/item/${itemID}`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const removeProduct = async (payload) => {
  let response = {};
  const { itemID } = payload;

  try {
    response = await axiosInstance.delete(`/webstore/cart/item/${itemID}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const paymentIntent = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/webstore/cart/checkout/paymentintent`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const fulfillTransaction = async (payload) => {
  let response = {};
  const { mailto } = payload;

  try {
    response = await axiosInstance.post(
      `/webstore/cart/checkout/paymentintent/success?mailto=${encodeURIComponent(mailto)}`,
      payload
    );
    // await deleteDataCache(axiosInstanceCached, true, 'v3_get_me');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const createSubscription = async (payload) => {
  let response = {};
  const { mailto, delayFirstPayment = false, delayTime = null, dualKey = null } = payload;

  const url = `/webstore/cart/checkout/subscription?mailto=${encodeURIComponent(
    mailto
  )}&delayStart=${delayFirstPayment}${
    delayTime ? `&delayTime=${encodeURIComponent(delayTime)}` : ''
  }${dualKey ? `&dualKey=${dualKey}` : ''}`;

  try {
    response = await axiosInstance.post(url, payload);
    // response = await axiosInstance.post(
    //   `/webstore/cart/checkout/subscription?mailto=${mailto}&delayStart=${delayFirstPayment}`,
    //   payload
    // );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getSubscription = async (transactionID = '') => {
  // @ /webstore/stripe/subscription/<stripeSubscriptionID>
  let response = {};

  try {
    response = await axiosInstance.get(`/webstore/stripe/subscription/${transactionID}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const cancelSubscription = async (payload = {}) => {
  // @ /webstore/stripe/subscription/<stripeSubscriptionID>
  let response = {};
  const { stripeSubscriptionID } = payload;

  try {
    response = await axiosInstance.delete(`/webstore/stripe/subscription/${stripeSubscriptionID}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ {
// @   "transactionID": "CLTGP-D3DBV-B6CJC-J47UG" example
// @   "subscriptionID": "sub_1Jhik9BvdHrUob1CZT8SJLHA" example
// @ }
export const updateTransactionID = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.put(`/transaction/${payload.transactionID}`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ {
// @   "transactionID": "CLTGP-D3DBV-B6CJC-J47UG" example
// @   "redeemedAt": "2022-11-07T22:53:12.604973Z" example
// @ }
export const updateTransactionDate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.put(`/transaction/${payload.transactionID}`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getOrderHistory = async (id = '') => {
  let response = {};

  try {
    response = await axiosInstance.get(`/webstore/order/user/${id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getOrderDetails = async (id = '') => {
  let response = {};

  try {
    response = await axiosInstance.get(`/webstore/order/${id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getTax = async (payload) => {
  let response = {};
  const url = `/webstore/cart/tax${payload?.readOnly ? `?readOnly=${payload?.readOnly}` : ''}`;

  try {
    response = await axiosInstance.post(url, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const resendOrderReceipt = async (payload) => {
  const { transactionID, email } = payload || {};
  let response = {};

  try {
    response = await axiosInstance.get(
      `/webstore/order/${transactionID}?mailto=${encodeURIComponent(email)}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const webhookLog = async ({ subID, retries = 100 }) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/webhooklog/subscription/${subID}`);

    if (response?.data?.length === 2 && response?.data?.[1]?.outcome === 'redeemed') {
      await deleteDataCache(axiosInstanceCached, true, 'list_licenses');
      return response;
    }

    if (retries) {
      await delay(1500);
      --retries;
      return webhookLog({ subID, retries });
    }
    response.data.webhookError = 'Did not pay. Attempts exhausted.';
    // throw new Error('Retry attempts exhausted');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const manageBilling = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/user_billing_sessions`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ payload { skucode: '123sdf' }
export const getLatestTransactionBySku = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/transaction_get_latest_by_sku`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ payload {
// @    skucode: '123sdf',
// @    subscription_id: 'sub_1QSNeHBucHqUob1Ca8ceA8hH',
// @ }
export const subscriptionUpgradeCheck = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/subscription_change_check`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ payload {
// @    sku: '123sdf',
// @ }
export const subscriptionUpgradeProductList = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/product_subscription_list`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ payload {
// @ skucode
// @ license_id
// @ }
export const addSpecialProduct = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/add_special_product`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};
